import { HStack } from '@/components/sgBootstrapReactWrapper';
import { useLazySearchPersonsQuery } from '@/store/api/person.api';
import { useLazySearchRootsQuery } from '@/store/api/root.api';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setPersonIdDisplayedInDrawer } from '@/store/persons/slice';
import { canSearchPersons, canSearchRoots, canSeeApplicationsLinks, canSeeAsmLink, getSelectedEntity, hasAccessToSeveralEntities } from '@/store/user/userSlice';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import { type ChangeEvent, type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Overlay } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useDebounceCallback } from 'usehooks-ts';
import { LegalEntitySelector } from '../LegalEntitySelector/LegalEntitySelector';
import { AdvancedSearchButton } from './AdvancedSearchButton';
import { AsmButton } from './AsmButton';
import { ResultsPanel, type TabId } from './ResultsPanel';

type Props = {
  initialTerm?: string;
  displayAsmButton?: boolean;
  displayEntitySelector?: boolean;
};

export const AccountSearchBar = ({ initialTerm = '', displayAsmButton = false, displayEntitySelector = true }: Props) => {
  const { $t } = useIntl();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [activeTab, setActiveTab] = useState<TabId>('roots');
  const [show, setShow] = useState(false);
  const [term, setTerm] = useState(initialTerm);

  const canSeeApplicationLinks = useAppSelector(canSeeApplicationsLinks)
  const canSeeAsmButton = useAppSelector(canSeeAsmLink)
  const hasAccessToMultipleEntities = useSelector(hasAccessToSeveralEntities)
  const canSearchRoot = useAppSelector(canSearchRoots)
  const canSearchPerson = useAppSelector(canSearchPersons)
  const selectedEntity = useAppSelector(getSelectedEntity)
  const dispatch = useAppDispatch()

  // biome-ignore lint/correctness/useExhaustiveDependencies: GH:issues/88 - when change location need to close the overlay
  useEffect(() => {
    setShow(false);
  }, [location]);

  const [queryRoots, { data: roots = [], isLoading: rootsAreLoading, error: rootsLoadingError }] = useLazySearchRootsQuery();

  const [
    queryPersons,
    { data: persons = [], isLoading: personsAreLoading, error: personsLoadingError },
  ] = useLazySearchPersonsQuery();

  const debouncedQueryRoots = useDebounceCallback(queryRoots, 300);
  const debouncedQueryPersons = useDebounceCallback(queryPersons, 300);

  const navigate = useNavigate()
  const searchIfEnter = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        if (roots.length === 1 && activeTab === 'roots') { // redirects to /root/$id
          navigate({
            to: '/root/$id',
            params: {
              id: roots[0].rootId
            }
          })
        }
        if (persons.length === 1 && activeTab === 'persons') { // opens person drawer
          dispatch(setPersonIdDisplayedInDrawer(persons[0].personId));
        }
      }
    },
    [roots, persons, activeTab],)

  const handleTermChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const searchTerm = e.currentTarget.value;
      setTerm(searchTerm);

      if (searchTerm.length === 0) {
        setShow(false);
      } else {
        // @TODO what if value.lenght is == 0 ? (when erase all value) need to setShow(false) ?
        setShow(true);
      }

      const trimmed = searchTerm.trim()
      if (trimmed.length < 2) {
        // @TODO need to invalid cache and return empty response
        return;
      }

      debouncedQueryRoots({ searchTerm: trimmed, entity: selectedEntity });
      debouncedQueryPersons({ searchTerm: trimmed, entity: selectedEntity });
    },
    [debouncedQueryRoots, debouncedQueryPersons, selectedEntity],
  );

  const tabList = <ul className="nav nav-underline nav-md border-bottom">
    <li
      data-e2e="root-tab"
      className="nav-item pt-32px ps-32px"
      onClick={() => setActiveTab('roots')}
    >
      <span
        className={clsx({
          'nav-link': true,
          'user-select-none': true,
          active: activeTab === 'roots',
        })}
      >
        <FormattedMessage id="root" />
      </span>
    </li>
    <li
      data-e2e="person-tab"
      className="nav-item pt-32px ps-32px"
      onClick={() => setActiveTab('persons')}
    >
      <span
        className={clsx({
          'nav-link': true,
          'user-select-none': true,
          active: activeTab === 'persons',
        })}
      >
        <FormattedMessage id="person" />
      </span>
    </li>
  </ul>
  const searchBar = <div className="position-relative flex-grow-1" ref={containerRef}>
    <div className="input-icon-start">
      <em className="icon">search</em>
    </div>
    <input
      type="search"
      data-e2e="account-search-bar"
      placeholder={$t({ id: 'searchAccountPlaceHolder' })}
      value={term}
      onChange={handleTermChange}
      onKeyUp={searchIfEnter}
      className="form-control form-control-lg h-100"
      ref={inputRef}
    />
    <Overlay
      show={show}
      placement="bottom"
      target={inputRef.current}
      container={containerRef} // Target a container to be inside - need to be relative
      rootClose
      rootCloseEvent="click"
      onHide={() => {
        return setShow(!show);
      }}
    >
      {({
        placement: _placement,
        arrowProps: _arrowProps,
        show: _show,
        popper: _popper,
        hasDoneInitialMeasure: _hasDoneInitialMeasure,
        ...props
      }) => (
        <div
          {...props}
          data-e2e="account-result-overlay"
          className="w-100"
          style={{
            ...props.style,
            // to appear over aggrid column resizers
            // document.querySelector(".ag-header-cell-resize").computedStyleMap().get('z-index')
            // => CSSUnitValue {value: 2, unit: 'number'}
            zIndex: 3,
          }}
        >
          <div className="card card-raised active bg-lvl1">
            {tabList}
            <div>
              <ResultsPanel
                {...{
                  displayMode: 'list',
                  activeTab,
                  emphasizedText: term,
                  roots,
                  persons,
                  rootsAreLoading,
                  personsAreLoading,
                  rootsLoadingError,
                  personsLoadingError,
                }}
              />
              {(roots.length > 0 || persons.length > 0) && (
                <HStack
                  horizontalAlignment="center"
                  className="py-24px ps-24px pe-16px text-secondary"
                  gap="8px"
                >
                  <em className="icon icon-md">search</em>

                  <Link
                    to="/searchResults"
                    search={(prev: any) => {
                      return {
                        ...prev,
                        searchTerm: term,
                        activeTab,
                      };
                    }}
                    onClick={() => {
                      return setShow(false);
                    }}
                  >
                    <span data-e2e="see-all-result" className="fs-14">
                      <FormattedMessage id="seeAllResults" />
                    </span>
                  </Link>
                </HStack>
              )}
            </div>
          </div>
        </div>
      )}
    </Overlay>
  </div>

  return <div className="input-group input-group-md d-flex justify-content-between">
    {hasAccessToMultipleEntities && displayEntitySelector && (<LegalEntitySelector />)}
    {(canSearchRoot || canSearchPerson) && (searchBar)}
    {canSeeApplicationLinks && (<AdvancedSearchButton />)}
    {canSeeAsmButton && displayAsmButton && (<AsmButton />)}
  </div>
};
